import { TableHead, TableRow } from '@talentmesh/ui/components/table';
import { cn } from '@talentmesh/ui/lib/utils';

import {
    type MRT_ColumnVirtualizer,
    type MRT_Header,
    type MRT_HeaderGroup,
    type MRT_RowData,
    type MRT_TableInstance,
    type MRT_VirtualItem,
} from '../../types';
import { type TableTrProps } from '../../types/mrt-ui-props';
import { parseFromValuesOrFunc } from '../../utils/utils';
import { MRT_TableFooterCell } from './MRT_TableFooterCell';
import classes from './MRT_TableFooterRow.module.css';

interface Props<TData extends MRT_RowData> extends TableTrProps {
    columnVirtualizer?: MRT_ColumnVirtualizer;
    footerGroup: MRT_HeaderGroup<TData>;
    table: MRT_TableInstance<TData>;
}

export const MRT_TableFooterRow = <TData extends MRT_RowData>({
    columnVirtualizer,
    footerGroup,
    table,
    ...rest
}: Props<TData>) => {
    const {
        options: { layoutMode, mantineTableFooterRowProps },
    } = table;

    const { virtualColumns, virtualPaddingLeft, virtualPaddingRight } = columnVirtualizer ?? {};

    if (
        !footerGroup.headers?.some(
            (header) =>
                (typeof header.column.columnDef.footer === 'string' && !!header.column.columnDef.footer) ||
                header.column.columnDef.Footer,
        )
    ) {
        return null;
    }

    const tableRowProps = {
        ...parseFromValuesOrFunc(mantineTableFooterRowProps, {
            footerGroup,
            table,
        }),
        ...rest,
    };

    return (
        <TableRow
            {...tableRowProps}
            className={cn(
                classes.root,
                layoutMode?.startsWith('grid') && classes['layout-mode-grid'],
                tableRowProps?.className,
            )}
        >
            {virtualPaddingLeft ? (
                <TableHead
                    aria-hidden
                    className="flex border-0 p-0"
                    style={{ minWidth: virtualPaddingLeft, width: virtualPaddingLeft }}
                />
            ) : null}
            {(virtualColumns ?? footerGroup.headers).map((footerOrVirtualFooter, renderedColumnIndex) => {
                let footer = footerOrVirtualFooter as MRT_Header<TData>;
                if (columnVirtualizer) {
                    renderedColumnIndex = (footerOrVirtualFooter as MRT_VirtualItem).index;
                    footer = footerGroup.headers[renderedColumnIndex];
                }

                return (
                    <MRT_TableFooterCell
                        footer={footer}
                        key={footer.id}
                        renderedColumnIndex={renderedColumnIndex}
                        table={table}
                    />
                );
            })}
            {virtualPaddingRight ? (
                <TableHead
                    aria-hidden
                    className="flex border-0 p-0"
                    style={{ minWidth: virtualPaddingRight, width: virtualPaddingRight }}
                />
            ) : null}
        </TableRow>
    );
};
